import React, { useEffect } from "react";
import { FiX } from "react-icons/fi";
import Button from "./Button";

/**
 * Accessible modal: backdrop blur, scale/fade animation, ESC to close,
 * body scroll lock while open. Presentation only — content comes from parent.
 */
export default function Modal({
    open,
    onClose,
    title,
    subtitle,
    children,
    footer,
    size = "md",
    closeOnBackdrop = true,
}) {
    useEffect(() => {
        if (!open) return;
        const onKey = (e) => {
            if (e.key === "Escape") onClose?.();
        };
        document.addEventListener("keydown", onKey);
        const prev = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => {
            document.removeEventListener("keydown", onKey);
            document.body.style.overflow = prev;
        };
    }, [open, onClose]);

    if (!open) return null;

    const sizes = {
        sm: "max-w-md",
        md: "max-w-lg",
        lg: "max-w-2xl",
        xl: "max-w-4xl",
    };

    return (
        <div className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center p-0 sm:p-4">
            <div
                className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm animate-fade-in"
                onClick={closeOnBackdrop ? onClose : undefined}
                aria-hidden="true"
            />
            <div
                role="dialog"
                aria-modal="true"
                aria-label={title}
                className={`relative w-full ${sizes[size] || sizes.md} max-h-[92vh] flex flex-col
                    rounded-t-2xl sm:rounded-2xl bg-white dark:bg-slate-900
                    border border-slate-200/80 dark:border-slate-800 shadow-pop
                    animate-scale-in overflow-hidden`}
            >
                {(title || onClose) && (
                    <div className="flex items-start justify-between gap-4 px-5 py-4 border-b border-slate-100 dark:border-slate-800">
                        <div className="min-w-0">
                            {title && (
                                <h3 className="text-base font-semibold text-slate-900 dark:text-slate-100">{title}</h3>
                            )}
                            {subtitle && (
                                <p className="mt-0.5 text-sm text-slate-500 dark:text-slate-400">{subtitle}</p>
                            )}
                        </div>
                        <Button variant="ghost" size="icon" onClick={onClose} aria-label="Tutup">
                            <FiX className="h-5 w-5" />
                        </Button>
                    </div>
                )}

                <div className="px-5 py-4 overflow-y-auto scrollbar-slim">{children}</div>

                {footer && (
                    <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-slate-100 dark:border-slate-800 bg-slate-50/60 dark:bg-slate-900/60">
                        {footer}
                    </div>
                )}
            </div>
        </div>
    );
}
